import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useHistory } from 'react-router-dom'

import { Col, Row, Table } from 'antd'
import { downloadCSV } from 'common'

import { AppConfigurationKeys, canCurrentUserSubmitExpenses, getAppConfiguration } from 'core'
import { BenefitInfo } from 'core/application/adminDashboard/benefitInfo'
import OrderedPageFilter from 'core/application/commons/orderedPageFilter'
import deleteExpenseApi from 'core/application/employeeDashboard/summary/reimbursementRequests/deleteExpense'
import downloadExpensesApi from 'core/application/employeeDashboard/summary/reimbursementRequests/downloadExpenses'
import getExpensesApi, {
  Expense,
  PagedExpenses,
} from 'core/application/employeeDashboard/summary/reimbursementRequests/getExpenses'

import Card from 'common/components/dataDisplay/Card'
import modal from 'common/components/feedback/Modal'
import notification from 'common/components/feedback/Notification'

import { commandRequest, queryRequest } from 'common/RequestUtils'

import ViewAttachmentsModal from '../../../common/components/expenseAttachments/ViewAttachmentsModal'
import Button from '../../../common/components/general/Button'
import { useQueryRequestV2 } from '../../../common/hooks'
import useQueryRequest from '../../../common/hooks/useQueryRequest'
import { ROUTE_PATHS } from '../../../common/routePaths'
import CardHeader from '../CardHeader'
import ReimbursementRequestCardMobile from '../reimbursementRequestsCard'
import ExpenseDetails from './ExpenseDetails'
import { reimbursementRequestsColumns } from './ReimbursementRequestsColumns'

import './ReimbursementRequests.less'

const pagedColumnDefaultValues = {
  items: [],
  pageCount: 1,
  pageNumber: 1,
  pageSize: 5,
  totalItemCount: 0,
}

const defaultFilter = {
  pageNumber: 1,
  pageSize: 40,
  orderColumn: 'CreatedAt',
  orderDescending: true,
}

type ReimbursementRequestsProps = {
  planPeriodId: string
}

const ReimbursementRequests: React.FC<ReimbursementRequestsProps> = ({ planPeriodId }) => {
  const { t: translate } = useTranslation(['ee-dashboard', 'translation'])
  const [expenses, setExpenses] = useState<PagedExpenses>(pagedColumnDefaultValues)
  const [loadingExpenses, setLoadingExpenses] = useState<boolean>(false)
  const [expensesFilter, setExpensesFilter] = useState<OrderedPageFilter>(defaultFilter)
  const [viewFilesVisible, setViewFilesVisible] = useState<boolean>(false)
  const [viewFilesForExpenseId, setViewFilesForExpenseId] = useState<string>()
  const [canUserSubmitExpenses, , sendCanUserSubmitExpenses] = useQueryRequest<boolean>(false)
  const history = useHistory()

  useEffect(() => {
    getExpenses()
  }, [expensesFilter, planPeriodId])

  const { data: docReviewTurnAroundTime } = useQueryRequestV2({
    queryFn: () => getAppConfiguration(AppConfigurationKeys.DocReviewTurnAroundInformation),
  })

  useEffect(() => {
    ;(async () => {
      if (planPeriodId) {
        await sendCanUserSubmitExpenses(() => canCurrentUserSubmitExpenses(planPeriodId))
      }
    })()
  }, [planPeriodId])

  const getExpenses = async () => {
    setLoadingExpenses(true)
    const result = await queryRequest(() => getExpensesApi(expensesFilter, planPeriodId ?? ''))
    setExpenses(result)
    setLoadingExpenses(false)
  }

  const onExpensesTableChange = (pagination: any, filters: any, sorter: any) => {
    setExpensesFilter({
      ...expensesFilter,
      pageNumber: pagination.current,
      pageSize: pagination.pageSize,
      orderColumn: sorter.field || 'createdAt',
      orderDescending: sorter.order !== 'ascend',
    })
  }

  const onDeleteExpense = (expenseId: string): void => {
    modal.confirm({
      title: translate('reimbursement-requests.confirm-remove-expense-title'),
      content: translate('reimbursement-requests.confirm-remove-expense-content'),
      okText: translate('translation:general.delete'),
      cancelText: translate('reimbursement-requests.confirm-keep-button'),
      onOk: async () => {
        const result = await commandRequest(() => deleteExpenseApi(expenseId))
        if (result.errors) {
          notification.open({
            message: result.errors[0],
            type: 'error',
          })
        } else {
          notification.open({
            type: 'success',
            message: translate('reimbursement-requests.expense-deleted-successfully-message'),
          })
          getExpenses()
        }
      },
    })
  }

  const onViewFilesForExpense = (expenseId: string): void => {
    setViewFilesVisible(true)
    setViewFilesForExpenseId(expenseId)
  }

  const downloadCsv = async () => {
    setLoadingExpenses(true)
    const result = await queryRequest(() => downloadExpensesApi(planPeriodId ?? ''))
    setLoadingExpenses(false)
    downloadCSV(result, `reimbursements.csv`)
  }

  const [windowWidth, setWindowWidth] = useState(window.innerWidth)
  const handleWindowResize = () => {
    setWindowWidth(window.innerWidth)
  }

  useEffect(() => {
    window.addEventListener('resize', handleWindowResize)
    return () => {
      window.removeEventListener('resize', handleWindowResize)
    }
  }, [])

  return (
    <Card
      title={<CardHeader title={translate('reimbursement-requests.title')} />}
      className="reimbursement_requests_summary"
    >
      <ViewAttachmentsModal
        visible={viewFilesVisible}
        onClose={() => {
          setViewFilesVisible(false)
        }}
        expenseId={viewFilesForExpenseId}
      />
      <Row justify="space-between">
        <Col className="mb-16" xs={24} sm={24} md={7} lg={7} xl={5}>
          <Button
            icon="fa fa-plus"
            iconPosition="left"
            onClick={() => {
              history.push(`${ROUTE_PATHS.ELIGIBLE_EMPLOYEE_GET_REIMBURSED}/${planPeriodId}`)
            }}
            htmlType="submit"
            type="primary"
            className="button__large"
            style={{ width: '100%' }}
            disabled={!canUserSubmitExpenses}
          >
            Get Reimbursed
          </Button>
        </Col>
        <Col className="mb-16" xs={24} sm={24} md={12} lg={12} xl={6}>
          <Row justify={windowWidth > 790 ? 'end' : 'start'}>
            <Button
              icon="far fa-download"
              iconPosition="left"
              type="primary"
              className="button__large"
              onClick={downloadCsv}
            >
              Download CSV
            </Button>
          </Row>
        </Col>
      </Row>
      {windowWidth > 790 ? (
        <Table
          dataSource={expenses.items}
          loading={loadingExpenses}
          scroll={{ x: 1000 }}
          onChange={onExpensesTableChange}
          columns={reimbursementRequestsColumns(
            translate,
            onDeleteExpense,
            onViewFilesForExpense,
            docReviewTurnAroundTime?.value,
          )}
          pagination={{
            total: expenses.totalItemCount,
            pageSize: expenses.pageSize,
            current: expenses.pageNumber,
          }}
          expandedRowRender={(expense: Expense) => {
            return <ExpenseDetails expense={expense} translate={translate} />
          }}
          rowKey={(key) => key.id}
          size="middle"
        />
      ) : (
        <ReimbursementRequestCardMobile
          reimbursementRequest={expenses}
          translate={translate}
          onDeleteExpense={onDeleteExpense}
          onViewFilesForExpense={onViewFilesForExpense}
        />
      )}
    </Card>
  )
}

export default ReimbursementRequests
